import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LockConflictError } from '@/lib/locks/types';
import { useCareRedeem } from '@/lib/disruptions/use-disruptions';
import type { CareCardRow } from '@/lib/disruptions/types';

// "Mark consumed" (#808, SKY PoC caso 2) — the counter marks the group's
// meal/care benefit as USED after the wallet validates the PRINTED coupon
// reference (PNR + reference is what a paper coupon binds by). The write is a
// simulation of consumption (the rehearsal runs with a mock issuer); the copy
// says so. Verdicts come from the wallet and keep the dialog open: 422 the
// reference is not this card's, 409 already consumed / voided. NEVER retry a
// POST automatically; any failure says "nothing was saved".
//
// `prefillReference` is set when the operator reached the dialog FROM a row
// they already identified (resolve panel / cards table); the counter lookup by
// reference path arrives with the keyed code itself.

/** Normalise keyed input like the wallet does (spaces, dashes, case). */
export function normalizeReference(input: string): string {
  return input.replace(/[^a-z0-9]/gi, '').toUpperCase();
}

interface IVerdict {
  status?: number;
  error?: string;
  redeemedAt?: string;
}

async function verdictOf(err: unknown): Promise<IVerdict> {
  const resp = (err as { response?: { status?: number; clone?: () => { json: () => Promise<unknown> } } })?.response;
  if (!resp) return {};
  const status: IVerdict = typeof resp.status === 'number' ? { status: resp.status } : {};
  try {
    const body = (await resp.clone?.().json()) as { error?: string; redemption?: { redeemedAt?: string } } | undefined;
    return {
      ...status,
      ...(body?.error ? { error: body.error } : {}),
      ...(body?.redemption?.redeemedAt ? { redeemedAt: body.redemption.redeemedAt } : {}),
    };
  } catch {
    return status;
  }
}

export function CareRedeemDialog({
  card,
  caseUrn,
  disruptionUrn,
  prefillReference,
  onClose,
  onRedeemed,
}: {
  card: Pick<CareCardRow, 'subCaseUrn' | 'pnrLabel' | 'holderMasked' | 'reference' | 'currency'>;
  caseUrn?: string | undefined;
  disruptionUrn: string;
  /** The reference to start with (the row's own, or the code the counter keyed). */
  prefillReference?: string;
  onClose: () => void;
  onRedeemed?: () => void;
}) {
  const { t } = useTranslation('care');
  const redeem = useCareRedeem(disruptionUrn);
  const [reference, setReference] = useState(prefillReference ?? '');
  const [amountRaw, setAmountRaw] = useState('');
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const normalized = normalizeReference(reference);
  const amount = amountRaw.trim() === '' ? undefined : Number(amountRaw);
  const amountValid = amount === undefined || (Number.isFinite(amount) && amount > 0);
  const canSubmit = normalized.length >= 4 && amountValid && !redeem.isPending;

  const confirm = async (): Promise<void> => {
    setFailure(null);
    try {
      await redeem.mutateAsync({
        subCaseUrn: card.subCaseUrn,
        ...(caseUrn ? { caseUrn } : {}),
        reference: normalized,
        ...(amount !== undefined ? { amount } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onRedeemed?.();
      onClose();
    } catch (err) {
      if (err instanceof LockConflictError) {
        setFailure(t('redeem.error.generic'));
        return;
      }
      const verdict = await verdictOf(err);
      if (verdict.status === 422) setFailure(t('redeem.error.referenceMismatch'));
      else if (verdict.error === 'already_redeemed') {
        setFailure(t('redeem.error.alreadyRedeemed', { when: verdict.redeemedAt ? new Date(verdict.redeemedAt).toLocaleString() : '—' }));
      } else if (verdict.error === 'card_voided') setFailure(t('redeem.error.cardVoided'));
      else if (verdict.status === 409) setFailure(t('redeem.error.cardNotActive'));
      else setFailure(t('redeem.error.generic'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md" data-testid="care-redeem-dialog">
        <DialogHeader>
          <DialogTitle>{t('redeem.title', { pnr: card.pnrLabel })}</DialogTitle>
          <DialogDescription data-testid="care-redeem-description">
            {t('redeem.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground" data-testid="care-redeem-holder">
            {t('lookup.match', { pnr: card.pnrLabel, holder: card.holderMasked })}
          </p>
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">{t('redeem.referenceLabel')}</span>
            <input
              type="text"
              autoFocus
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('redeem.referencePlaceholder')}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono uppercase tracking-[0.15em]"
              aria-label={t('redeem.referenceLabel')}
              data-testid="care-redeem-reference"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs uppercase text-muted-foreground">{t('redeem.amountLabel')}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={amountRaw}
                onChange={(e) => setAmountRaw(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 tabular-nums"
                aria-label={t('redeem.amountLabel')}
                data-testid="care-redeem-amount"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs uppercase text-muted-foreground">{t('redeem.noteLabel')}</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('redeem.notePlaceholder')}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5"
                aria-label={t('redeem.noteLabel')}
                data-testid="care-redeem-note"
              />
            </label>
          </div>
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="care-redeem-simulated">
            {t('redeem.simulated')}
          </p>
          {failure ? (
            <p className="text-xs text-destructive" role="alert" data-testid="care-redeem-failure">
              {failure}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="care-redeem-cancel">
            {t('redeem.cancel')}
          </Button>
          <Button onClick={confirm} disabled={!canSubmit} data-testid="care-redeem-submit">
            {redeem.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
            {t('redeem.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
